"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";

/**
 * Portaled Radix layers that a tap DISMISSES — dropdown/context menus
 * (`menu`), selects and comboboxes (`listbox`), and popovers (`dialog`).
 *
 * Every entry is anchored to a Radix-owned marker, never to a bare ARIA role.
 * A role on its own is not enough: plenty of ordinary in-flow markup is a
 * `listbox` without being a dismissable layer at all. The dispatch map's
 * address bar renders its suggestions as `<ul role="listbox">` inline, and it
 * stays up until the query is cleared or a suggestion is picked — so a bare
 * `[role="listbox"]` match meant that, with suggestions showing, EVERY drawer
 * and dialog on the board refused to close on an outside tap, for any number
 * of taps, because nothing was dismissing the `<ul>` either.
 *
 * The two markers used here:
 *
 * - `[data-radix-popper-content-wrapper]` — the popper-positioned layers. The
 *   role is still required inside it, because tooltips and hover cards ride
 *   the very same wrapper and are NOT dismissed by a tap (they're gone the
 *   moment the pointer leaves). Letting an open tooltip swallow a deliberate
 *   tap-away would be a worse bug than the one being fixed here.
 * - `[data-radix-select-viewport]` — a select rendered `position="item-aligned"`
 *   sits outside the popper wrapper entirely. Radix sets this attribute on the
 *   viewport unconditionally, in both position modes, so it catches that case
 *   without matching anything the app itself renders.
 */
const NESTED_LAYER_SELECTOR = ['[data-radix-popper-content-wrapper] [role="menu"]', '[data-radix-popper-content-wrapper] [role="listbox"]', '[data-radix-popper-content-wrapper] [role="dialog"]', "[data-radix-select-viewport]"].join(",");

/** Whether any portaled Radix layer is currently mounted in the document. */
function hasOpenNestedLayer() {
  if (typeof document === "undefined") return false;
  return document.querySelector(NESTED_LAYER_SELECTOR) !== null;
}

/**
 * Whether a portaled Radix layer was up at the last `pointerdown` — the raw
 * reading the guards below are built on, for callers whose dismissal isn't a
 * cancelable `Event` they can `preventDefault()`.
 *
 * SAMPLED ON POINTERDOWN, not read when the caller fires. On touch Radix defers
 * its outside-dismissal to the `click` that follows the `pointerdown`, and by
 * then the layer has often already unmounted — a caller reading live would look
 * at a clean document, conclude nothing was open, and act anyway. The
 * pointerdown is the one moment both the layer and the caller's surface are
 * provably still up. Capture phase, so the sample beats every handler that
 * could close the layer being sampled for.
 *
 * Every pointerdown re-samples, so a caller that skips one interaction is back
 * to normal on the next.
 */
export function useNestedLayerWasOpenAtPress() {
  const $ = _c(3);
  const layerWasOpenRef = React.useRef(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const sample = () => {
        layerWasOpenRef.current = hasOpenNestedLayer();
      };
      document.addEventListener("pointerdown", sample, true);
      return () => {
        document.removeEventListener("pointerdown", sample, true);
        layerWasOpenRef.current = false;
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  React.useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => layerWasOpenRef.current;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

/**
 * Stops a dialog / drawer / sheet from being dismissed by the very tap that
 * dismisses a menu, select or popover opened INSIDE it.
 *
 * Those layers portal to `document.body`, so neither their content nor the
 * empty space around them is a descendant of the container. A tap off an open
 * menu is therefore an outside interaction for the container as well as for
 * the menu, and both dismiss — the sheet closes out from under a user who was
 * only putting a dropdown away, losing whatever they had part-way filled in.
 *
 * Radix's own layer stack is supposed to prevent exactly this (a layer only
 * dismisses when nothing above it has disabled outside pointer events), and on
 * a plain dialog it does. It does not hold up across every
 * portal/modality/container combination this app puts these controls in — the
 * board's drawers reproduce it with dropdowns, selects and popovers alike — so
 * the container guards itself rather than trusting the stack.
 *
 * Sampled on pointerdown for the reason `useNestedLayerWasOpenAtPress` (above)
 * explains.
 *
 * Returns a handler for `onInteractOutside`, which covers both the pointer and
 * the focus route to the same dismissal. Compose it with a caller's own
 * handler; a caller that has already called `preventDefault()` is left alone.
 *
 * Takes no open/closed argument on purpose. The obvious version — attach only
 * while the container is open — cannot work from here: `DialogContent` and
 * friends sit OUTSIDE their own `Portal`, so their bodies run whenever the
 * consumer renders them, and the standard shape in this app is to keep them
 * mounted (`<Sheet open={open}><SheetContent …>`). An `open` parameter would
 * therefore be `true` at every call site whether or not the container was
 * showing — a gate that reads as cost control while gating nothing. The real
 * cost is one capture-phase listener and one `querySelector` per pointer press
 * per mounted container, which is not worth restructuring the primitives for.
 */
export function useNestedLayerDismissGuard() {
  const $ = _c(2);
  const layerWasOpenAtPress = useNestedLayerWasOpenAtPress();
  let t0;
  if ($[0] !== layerWasOpenAtPress) {
    t0 = event => {
      if (layerWasOpenAtPress()) {
        event.preventDefault();
      }
    };
    $[0] = layerWasOpenAtPress;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}